import { useState } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

/**
 * ETAPA 12 — BUSCA E FILTROS DA LISTAGEM PÚBLICA DE CAMPEONATOS
 *
 * Desenho: uma BUSCA em destaque (porque é o que a maioria usa) e os filtros
 * atrás de um botão "Filtros", que fica FECHADO por padrão. Assim a seção não
 * vira um painel grande quando ninguém está filtrando — que era a preocupação
 * principal.
 *
 * Os filtros só aparecem se houver dado para eles: as opções vêm do próprio
 * banco (`/public/championships/filtros`), então não existe a situação de o
 * visitante escolher um filtro que não devolve nada.
 *
 * Cada filtro ativo vira uma "etiqueta" com um X para remover SOZINHO, sem
 * mexer nos outros — além do "Limpar tudo".
 */

const MODALIDADES = [
  { valor: 'gi', rotulo: 'Kimono (Gi)' },
  { valor: 'nogi', rotulo: 'No-Gi' },
  { valor: 'misto', rotulo: 'Misto (as duas)' },
];

const SITUACOES = [
  { valor: 'abertas', rotulo: 'Inscrições abertas e em andamento' },
  { valor: 'open', rotulo: 'Só inscrições abertas' },
  { valor: 'in_progress', rotulo: 'Só em andamento' },
  { valor: 'finished', rotulo: 'Só finalizados' },
  { valor: 'todos', rotulo: 'Todas as situações' },
];

/* Mes sozinho = "aquele mes em qualquer ano" (o backend suporta os dois casos). */
const MESES = [
  { valor: '1', rotulo: 'Janeiro' }, { valor: '2', rotulo: 'Fevereiro' },
  { valor: '3', rotulo: 'Março' }, { valor: '4', rotulo: 'Abril' },
  { valor: '5', rotulo: 'Maio' }, { valor: '6', rotulo: 'Junho' },
  { valor: '7', rotulo: 'Julho' }, { valor: '8', rotulo: 'Agosto' },
  { valor: '9', rotulo: 'Setembro' }, { valor: '10', rotulo: 'Outubro' },
  { valor: '11', rotulo: 'Novembro' }, { valor: '12', rotulo: 'Dezembro' },
];

const vazio = (v) => v === '' || v === null || v === undefined;

export function FiltrosEventos({ busca, aoBuscar, filtros, aoFiltrar, aoLimpar, opcoes }) {
  const [painelAberto, setPainelAberto] = useState(false);

  const ativos = [
    !vazio(filtros.ano) && { chave: 'ano', texto: `${filtros.ano}` },
    !vazio(filtros.mes) && {
      chave: 'mes',
      texto: MESES.find((m) => m.valor === String(filtros.mes))?.rotulo || `Mês ${filtros.mes}`,
    },
    !vazio(filtros.modalidade) && {
      chave: 'modalidade',
      texto: MODALIDADES.find((m) => m.valor === filtros.modalidade)?.rotulo || filtros.modalidade,
    },
    !vazio(filtros.federacao) && {
      chave: 'federacao',
      texto: opcoes.federacoes.find((f) => f._id === filtros.federacao)?.name || 'Federação',
    },
    !vazio(filtros.situacao) && {
      chave: 'situacao',
      texto: SITUACOES.find((s) => s.valor === filtros.situacao)?.rotulo || filtros.situacao,
    },
  ].filter(Boolean);

  const temBusca = !vazio(busca);

  return (
    <div className="mb-6" data-testid="filtros-eventos">
      {/* BUSCA em destaque + botão de filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" aria-hidden="true" />
          <Input
            value={busca}
            onChange={(e) => aoBuscar(e.target.value)}
            placeholder="Buscar por nome, cidade ou endereço do campeonato"
            aria-label="Buscar campeonatos"
            data-testid="busca-campeonatos"
            className="pl-9 h-10 bg-[#141414] border-[#27272A] text-white placeholder:text-[#71717A]"
          />
          {temBusca && (
            <button
              type="button"
              onClick={() => aoBuscar('')}
              aria-label="Limpar busca"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white"
              data-testid="limpar-busca"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={() => setPainelAberto((v) => !v)}
          aria-expanded={painelAberto}
          data-testid="toggle-filtros"
          className="border-[#27272A] text-[#A1A1AA] hover:text-white hover:bg-[#141414] h-10 shrink-0"
        >
          <SlidersHorizontal size={16} className="mr-2" />
          Filtros
          {ativos.length > 0 && (
            <span className="ml-2 rounded-full bg-[#D4AF37] text-black text-micro font-bold px-1.5 py-0.5">
              {ativos.length}
            </span>
          )}
        </Button>
      </div>

      {/* PAINEL — só quando pedido */}
      {painelAberto && (
        <div
          className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 rounded-xl border border-[#27272A] bg-[#141414] p-4"
          data-testid="painel-filtros"
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-micro uppercase tracking-wider text-[#71717A]">Ano</span>
            <Select value={filtros.ano || 'todos'} onValueChange={(v) => aoFiltrar('ano', v === 'todos' ? '' : v)}>
              <SelectTrigger data-testid="filtro-ano" className="bg-[#0A0A0A] border-[#27272A] text-white">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="todos" className="text-white hover:bg-[#27272A]">Todos os anos</SelectItem>
                {opcoes.anos.map((a) => (
                  <SelectItem key={a} value={String(a)} className="text-white hover:bg-[#27272A]">{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-micro uppercase tracking-wider text-[#71717A]">Mês</span>
            <Select value={filtros.mes ? String(filtros.mes) : 'todos'} onValueChange={(v) => aoFiltrar('mes', v === 'todos' ? '' : v)}>
              <SelectTrigger data-testid="filtro-mes" className="bg-[#0A0A0A] border-[#27272A] text-white">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="todos" className="text-white hover:bg-[#27272A]">Todos os meses</SelectItem>
                {MESES.map((m) => (
                  <SelectItem key={m.valor} value={m.valor} className="text-white hover:bg-[#27272A]">{m.rotulo}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-micro uppercase tracking-wider text-[#71717A]">Modalidade</span>
            <Select value={filtros.modalidade || 'todas'} onValueChange={(v) => aoFiltrar('modalidade', v === 'todas' ? '' : v)}>
              <SelectTrigger data-testid="filtro-modalidade" className="bg-[#0A0A0A] border-[#27272A] text-white">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="todas" className="text-white hover:bg-[#27272A]">Todas</SelectItem>
                {MODALIDADES.map((m) => (
                  <SelectItem key={m.valor} value={m.valor} className="text-white hover:bg-[#27272A]">{m.rotulo}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-micro uppercase tracking-wider text-[#71717A]">Federação</span>
            <Select value={filtros.federacao || 'todas'} onValueChange={(v) => aoFiltrar('federacao', v === 'todas' ? '' : v)}>
              <SelectTrigger data-testid="filtro-federacao" className="bg-[#0A0A0A] border-[#27272A] text-white">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="todas" className="text-white hover:bg-[#27272A]">Todas as federações</SelectItem>
                {opcoes.federacoes.map((f) => (
                  <SelectItem key={f._id} value={f._id} className="text-white hover:bg-[#27272A]">{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-micro uppercase tracking-wider text-[#71717A]">Situação</span>
            <Select value={filtros.situacao || 'abertas'} onValueChange={(v) => aoFiltrar('situacao', v === 'abertas' ? '' : v)}>
              <SelectTrigger data-testid="filtro-situacao" className="bg-[#0A0A0A] border-[#27272A] text-white">
                <SelectValue placeholder="Abertas" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                {SITUACOES.map((s) => (
                  <SelectItem key={s.valor || 'padrao'} value={s.valor || 'abertas'} className="text-white hover:bg-[#27272A]">{s.rotulo}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        </div>
      )}

      {/* ETIQUETAS dos filtros ativos — cada uma sai sozinha */}
      {ativos.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="filtros-ativos">
          {ativos.map((a) => (
            <button
              key={a.chave}
              type="button"
              onClick={() => aoFiltrar(a.chave, '')}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1 text-xs text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/20"
              title={`Remover filtro: ${a.texto}`}
              data-testid={`remover-filtro-${a.chave}`}
            >
              {a.texto}
              <X size={12} aria-hidden="true" />
            </button>
          ))}
          <button
            type="button"
            onClick={() => { aoLimpar(); setPainelAberto(false); }}
            className="text-xs text-[#A1A1AA] underline decoration-dotted underline-offset-2 transition-colors hover:text-white"
            data-testid="limpar-filtros"
          >
            Limpar tudo
          </button>
        </div>
      )}
    </div>
  );
}

export default FiltrosEventos;
